/* eslint-disable @nx/enforce-module-boundaries */
import { defineStyleConfig, StyleFunctionProps, theme } from '@chakra-ui/react';

const primary = (props: StyleFunctionProps) => ({
  ...theme.components.Button.variants?.solid(props),
  bg: 'brand.solid-primary',
  color: 'misc.white-fix-primary',
  borderColor: 'transparent',
  _hover: {
    bg: ['brand.solid-primary', 'brand.solid-hover'],
    _disabled: {
      bg: 'neutral.fill-subtle',
      color: 'neutral.fg-disabled',
    },
  },
  _active: {
    bg: 'brand.solid-hover',
  },
  _disabled: {
    bg: 'neutral.fill-subtle',
    color: 'neutral.fg-disabled',
  },
});

export const Button = defineStyleConfig({
  baseStyle: {
    fontWeight: 'bold',
    border: '0.1',
    height: 'auto',
    borderRadius: 'lg',
    _focusVisible: {
      outline: 0,
      boxShadow: '0 0 0 1px white, 0 0 0 3px #6691FF',
    },
  },
  variants: {
    primary,
    unstyled: {
      ...theme.components.Button.variants?.unstyled,
      borderStyle: 'none',
    },
    link: {
      ...theme.components.Button.variants?.link,
      borderStyle: 'none',
    },
    ghost: {
      ...theme.components.Button.variants?.ghost,
      borderStyle: 'none',
    },
    'republic-default': {
      bg: 'neutral.bg-primary',
      boxShadow: 'inset 0 0 0 1px #c7c7c7',
      color: 'neutral.fg-primary',
      borderStyle: 'none',
      _hover: {
        borderColor: 'neutral.border-base',
        boxShadow: 'inset 0 0 0 1px #0049FF',
        color: 'brand.fg-primary',
        _disabled: {
          boxShadow: 'inset 0 0 0 1px #e0e0e0',
          color: 'neutral.fg-disabled',
        },
      },
      _disabled: {
        opacity: '1',
        boxShadow: 'inset 0 0 0 1px #e0e0e0',
        color: 'neutral.fg-disabled',
      },
    },
    'republic-primary': {
      bg: 'brand.solid-primary',
      color: 'misc.white-fix-primary',
      borderStyle: 'none',
      _hover: {
        bgColor: 'brand.solid-hover',
        _disabled: {
          bg: 'neutral.fill-subtle',
          color: 'neutral.fg-disabled',
          opacity: '1',
        },
      },
      _disabled: {
        bg: 'neutral.fill-subtle',
        color: 'neutral.fg-disabled',
        opacity: '1',
      },
    },
    'republic-secondary': (props: StyleFunctionProps) => ({
      ...theme.components.Button.variants?.outline(props),
      bg: 'neutral.bg-primary',
      color: 'neutral.fg-primary',
      borderColor: 'neutral.border-base',
      _hover: {
        color: 'brand.fg-primary',
        borderColor: 'brand.border-base',
        _disabled: {
          bg: 'neutral.bg-primary',
          color: 'neutral.fg-disabled',
          borderColor: 'neutral.border-base',
        },
      },
      _active: {
        bg: 'neutral.bg-tertiary',
        color: 'brand.fg-primary',
        borderColor: 'brand.border-base',
      },
      _disabled: {
        bg: 'neutral.bg-primary',
        color: 'neutral.fg-disabled',
      },
    }),

    'republic-tertiary': (props: StyleFunctionProps) => ({
      ...theme.components.Button.variants?.outline(props),
      px: '5',
      py: '2',
      border: '0',
      minW: 'auto',
      color: 'brand.fg-primary',
      textDecoration: 'underline',
      textUnderlineOffset: 3,
      _hover: {
        color: 'brand.fg-secondary',
        _disabled: {
          color: 'neutral.fg-disabled',
        },
      },
      _active: {
        color: 'brand.fg-primary',
      },
      _disabled: {
        color: 'neutral.fg-disabled',
      },
    }),
    'republic-toggle': {
      bg: 'grey.100',
      fontSize: 'xs',
      fontWeight: 'semibold',
    },
    'republic-danger': {
      bg: 'red.800',
      color: 'white',
      _hover: {
        _disabled: {
          bg: 'red.800',
          opacity: 0.4,
        },
      },
      _disabled: {
        bg: 'red.800',
        opacity: 0.4,
      },
    },
    'republic-viewContents': {
      p: 0,
      px: 0,
      height: 'unset',
      minW: 'unset',
    },
  },
  sizes: {
    'republic-default': {
      py: '3',
      px: '6',
    },
    default: {
      fontSize: 'md',
      fontWeight: 'semibold',
      lineHeight: 'short',
      h: '12',
      py: '3',
      px: '4',
    },
    'republic-large': {
      fontSize: '22px',
      lineHeight: 'short',
      py: '5',
      px: '8',
      borderRadius: 'xl',
    },
  },
  defaultProps: {
    size: 'default',
    variant: 'primary',
  },
});
